import { useCallback, useMemo } from "react"

import { Filter, Sort } from "../../../api/GridRequest"
import { Column, ColumnFilters, ColumnName } from "../../../api/Randoms/SelectPoolForRunGridColumns"
import Checkbox from "../../Checkbox"
import ColumnHeaderFilter, { ValueLabels } from "../../ColumnHeaderFilter"

const HEADER_CELL_STYLE: Partial<Record<ColumnName, string>> = {
  SelectionInterval: "text-center",
  LastSelection: "text-center",
  AlcoholSampleSize: "text-center",
  DrugSampleSize: "text-center",
}

const MAP_VALUE_LABELS: ValueLabels = {
  "N/P": "Not Provided",
}

type OrderType = "asc" | "desc" | undefined

interface Filters extends Partial<ColumnFilters> {
  PoolName: string[]
  SelectionInterval: string[]
}

interface SelectPoolForRunGridHeaderProps {
  columns: Column[]
  filtersData: Filters
  filters: Filter[]
  allPoolsChecked: boolean
  sort: Sort
  isConsortium: boolean
  readonly?: boolean
  onAllPoolsChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSortChange: (order?: string, column?: string) => void
  onFilterChange: (column: string, items: string[]) => void
}
function SelectPoolForRunGridHeader({
  columns,
  filtersData,
  filters,
  allPoolsChecked,
  sort,
  isConsortium,
  readonly = false,
  onAllPoolsChange,
  onSortChange,
  onFilterChange,
}: SelectPoolForRunGridHeaderProps) {
  const formatColumnLabel = useCallback(
    (column: Column) => {
      const baseLabel = column.label ?? ""

      if (["Name", "Code"].includes(baseLabel)) {
        return `${isConsortium ? "Consortium" : "Pool"} ${baseLabel}`
      }

      return baseLabel
    },
    [isConsortium]
  )

  const headerCells = useMemo(
    () =>
      columns.map((column) => {
        const { name, filter } = column
        const isFiltered = filters.some((f) => f.column === filter)
        const currentFilter = filters.find((f) => f.column === filter)?.values ?? []
        const sortOrder = sort.column === name ? (sort.order as OrderType) : undefined

        return (
          <th key={name} className={HEADER_CELL_STYLE[name]}>
            {filter ? (
              <ColumnHeaderFilter
                title={formatColumnLabel(column)}
                items={filtersData[filter] || []}
                filteredItems={currentFilter}
                filtered={isFiltered}
                sort={sortOrder}
                itemValueLabels={MAP_VALUE_LABELS}
                onSort={(order) => onSortChange(order, name)}
                onFilter={(items) => onFilterChange(filter, items)}
                offsetTop={15}
                uppercase={false}
              />
            ) : (
              formatColumnLabel(column)
            )}
          </th>
        )
      }),
    [columns, filters, filtersData, sort, formatColumnLabel, onSortChange, onFilterChange]
  )

  return (
    <tr>
      {!readonly && (
        <th className="text-center">
          <Checkbox id="chb_all_pool" checked={allPoolsChecked} onChange={onAllPoolsChange} />
        </th>
      )}
      {headerCells}
    </tr>
  )
}

export default SelectPoolForRunGridHeader
